import React from 'react';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import { AuthPermissions } from 'skipify-types';

import { Button } from '../../../components/core';
import { SearchTextField } from '../components';
import { FiltersButton } from '../components/FiltersButton';
import { RefreshButton } from '../components/RefreshButton';
import { useMerchant } from '../../../contexts/MerchantContext';
import appTheme from '../../../styles/themes/theme';

interface Props {
  refetchPaylinks: () => void;
  isFetching: boolean;
  setIsDownloadModalOpen: (open: boolean) => void;
  setIsSendModalOpen: (open: boolean) => void;
  onSearch: (query: string) => void;
  searchQuery?: string;
  searchResetKey?: number;
  setIsFiltersPanelOpen: (isFiltersPanelOpen: boolean) => void;
  filtersCount: number;
}

export const TableHeader = ({
  refetchPaylinks,
  isFetching,
  setIsDownloadModalOpen,
  setIsSendModalOpen,
  onSearch,
  searchQuery = '',
  searchResetKey = 0,
  setIsFiltersPanelOpen,
  filtersCount,
}: Props) => {
  const { canAccess } = useMerchant();
  const isMobile = useMediaQuery(appTheme.breakpoints.down('sm'));

  if (isMobile) {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            '& button': { height: '50px', px: 4 },
          }}
        >
          <Typography component="h2" fontWeight="semibold" variant="large">
            View all Paylinks
          </Typography>
          <Button
            text="Send Paylink"
            onClick={() => setIsSendModalOpen(true)}
            disabled={
              !canAccess(AuthPermissions.CREATE_PAYLINKS) &&
              !canAccess(AuthPermissions.CREATE_OWN_PAYLINKS)
            }
          />
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 2,
            '& button': { height: '50px' },
          }}
        >
          <Button
            text="Export"
            theme="secondary"
            onClick={() => setIsDownloadModalOpen(true)}
            disabled={!canAccess(AuthPermissions.DOWNLOAD_PAYLINKS)}
          />
          <FiltersButton onClick={() => setIsFiltersPanelOpen(true)} filtersCount={filtersCount} />
          <RefreshButton onClick={refetchPaylinks} disabled={isFetching} />
        </Box>
        <Box>
          <SearchTextField
            label="Search by Paylink details"
            onSearch={onSearch}
            defaultValue={searchQuery}
            resetKey={searchResetKey}
          />
        </Box>
      </>
    );
  }
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Stack direction="row" gap={2} sx={{ alignItems: 'center' }}>
          <Typography component="h2" fontWeight="semibold" variant="xl">
            View all Paylinks
          </Typography>
          <RefreshButton onClick={refetchPaylinks} disabled={isFetching} />
        </Stack>
        <Stack direction="row" gap={2} sx={{ '&  button': { px: 5, py: 1.5 } }}>
          <Button
            text="Export Paylinks"
            theme="secondary"
            onClick={() => setIsDownloadModalOpen(true)}
            disabled={!canAccess(AuthPermissions.DOWNLOAD_PAYLINKS)}
          />
          <Button
            text="Send Paylink"
            onClick={() => setIsSendModalOpen(true)}
            disabled={
              !canAccess(AuthPermissions.CREATE_PAYLINKS) &&
              !canAccess(AuthPermissions.CREATE_OWN_PAYLINKS)
            }
          />
        </Stack>
      </Box>
      <Box sx={{ display: 'flex', gap: 3 }}>
        <SearchTextField
          // TODO: Add Amount back once bug on Paylink API is fixed
          // label="Search by customer details, merchant name, Skipify OrderID, Paylink ID or amount"
          label="Search by customer details, merchant name, Skipify OrderID, or Paylink ID"
          onSearch={onSearch}
          defaultValue={searchQuery}
          resetKey={searchResetKey}
        />
        <FiltersButton onClick={() => setIsFiltersPanelOpen(true)} filtersCount={filtersCount} />
      </Box>
    </>
  );
};
